import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { DesktopLayout, StandingsSidebar, LiveAnswer as SharedLiveAnswer, AnswerOptions, ImageWithFallback, QuestionDisplay, TimerSection } from '../shared';
import { useQuestionFlip, useTimerFlipTrigger } from '../../hooks/useQuestionFlip';
import { Question } from '../../types/game';
import { GameEndScreen } from './GameEndScreen';
import type { GameStatistics } from '../../types/game';
import '../../App.css';
import '../../styles/game-layout.css';
import { useTranslation } from '../../i18n';

interface KinkyOrNotQuestion {
  questionId?: string;
  id?: string;
  question: string;
  options: string[];
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  imageUrl?: string;
  fallbackImageUrl?: string;
  isPicturePerfect?: boolean;
  isKinkyOrNot?: boolean;
  interesting?: string;
}

interface QuestionResult {
  questionId: string;
  correctAnswer: string;
  submissions: Array<{
    playerId: string;
    answer: string;
    responseTime: number;
    isCorrect: boolean;
    points: number;
  }>;
  leaderboard: Array<{
    playerId: string;
    playerName: string;
    score: number;
  }>;
  isKinkyOrNot?: boolean;
}

interface LiveAnswer {
  playerId: string;
  playerName: string;
  avatar?: string;
  timestamp: number;
  answer?: string;
}

interface KinkyOrNotHostDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: KinkyOrNotQuestion;
  };
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
}

// Theme colors for Kinky or Not (Party theme - pink/red)
const THEME = {
  primary: '#EC4899',
  primaryLight: 'rgba(236, 72, 153, 0.15)',
  primaryBorder: 'rgba(236, 72, 153, 0.3)',
  accent: '#F59E0B',
  accentLight: 'rgba(245, 158, 11, 0.15)',
  accentBorder: 'rgba(245, 158, 11, 0.3)',
};

const KinkyOrNotHostDisplay: React.FC<KinkyOrNotHostDisplayProps> = ({
  roomState,
  socket,
  onBackToGameSelection,
  myPlayerId,
  hostIsPlaying = false,
  onGoHome,
}) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState<KinkyOrNotQuestion | null>(
    roomState.currentQuestion || null
  );
  const [timeRemaining, setTimeRemaining] = useState<number>(
    roomState.currentQuestion?.timeLimit || 0
  );
  const [questionResult, setQuestionResult] = useState<QuestionResult | null>(null);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const [waitingForNewGame, setWaitingForNewGame] = useState(false);
  const [liveAnswers, setLiveAnswers] = useState<LiveAnswer[]>([]);

  // Host playing mode state
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [answerCorrect, setAnswerCorrect] = useState<boolean | null>(null);
  const [pointsEarned, setPointsEarned] = useState(0);
  const isSubmittingRef = useRef<boolean>(false);

  const previousQuestionIdRef = useRef<string | undefined>(undefined);
  const roomNameRef = useRef<string>(roomState.roomName);
  const playersRef = useRef(roomState.players);

  // Question flip animation
  const flipState = useQuestionFlip({
    currentQuestion,
    liveAnswers,
    totalPlayers: roomState.players.length,
    answerSubmitted,
    hostIsPlaying,
  });

  // Trigger flip when timer runs out
  useTimerFlipTrigger(
    timeRemaining,
    currentQuestion,
    flipState.showQuestionFlip,
    flipState.setShowQuestionFlip,
    flipState.previousQuestionIdForFlipRef
  );

  const resetHostPlayingState = () => {
    setSelectedAnswer(null);
    setAnswerSubmitted(false);
    setAnswerCorrect(null);
    setPointsEarned(0);
    setLiveAnswers([]);
    isSubmittingRef.current = false;
  };

  useEffect(() => {
    roomNameRef.current = roomState.roomName;
  }, [roomState.roomName]);

  useEffect(() => {
    playersRef.current = roomState.players;
  }, [roomState.players]);

  useEffect(() => {
    const questionId = roomState.currentQuestion?.questionId || roomState.currentQuestion?.id;
    if (previousQuestionIdRef.current !== questionId && questionId) {
      setCurrentQuestion(roomState.currentQuestion || null);
      setTimeRemaining(roomState.currentQuestion?.timeLimit || 0);
      setQuestionResult(null);
      resetHostPlayingState();
      previousQuestionIdRef.current = questionId;
    }
  }, [roomState.currentQuestion]);

  useEffect(() => {
    const handleQuestion = (data: KinkyOrNotQuestion) => {
      if (data.isKinkyOrNot || data.isPicturePerfect) {
        setCurrentQuestion(data);
        setTimeRemaining(data.timeLimit);
        setQuestionResult(null);
        resetHostPlayingState();
        previousQuestionIdRef.current = data.questionId || data.id;
      }
    };

    const handleQuestionResult = (result: QuestionResult) => {
      if (result.isKinkyOrNot) {
        setQuestionResult(result);
        setCurrentQuestion(null);
      }
    };

    const handleGameEnded = (data: {
      leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
      statistics?: GameStatistics;
    }) => {
      setGameEnded(true);
      const sortedLeaderboard = [...(data.leaderboard || [])].sort((a, b) => b.score - a.score);
      setFinalLeaderboard(sortedLeaderboard);
      setGameStatistics(data.statistics || null);
      setCurrentQuestion(null);
      setQuestionResult(null);
    };

    const handleReturnToWaiting = () => {
      setWaitingForNewGame(true);
      setGameEnded(false);
      setCurrentQuestion(null);
      setQuestionResult(null);
      setGameStatistics(null);
    };

    const handlePlayerAnswered = (data: LiveAnswer) => {
      setLiveAnswers((prev) => {
        if (prev.some((a) => a.playerId === data.playerId)) return prev;
        return [...prev, data];
      });
    };

    const handleAnswerReceived = (data: {
      questionId: string;
      isCorrect: boolean;
      points: number;
    }) => {
      if (hostIsPlaying) {
        setAnswerCorrect(data.isCorrect);
        setPointsEarned(data.points);
        setAnswerSubmitted(true);
      }
    };

    socket.on('question', handleQuestion);
    socket.on('question-result', handleQuestionResult);
    socket.on('game-ended', handleGameEnded);
    socket.on('return-to-waiting', handleReturnToWaiting);
    socket.on('player-answered', handlePlayerAnswered);
    socket.on('answer-received', handleAnswerReceived);

    return () => {
      socket.off('question', handleQuestion);
      socket.off('question-result', handleQuestionResult);
      socket.off('game-ended', handleGameEnded);
      socket.off('return-to-waiting', handleReturnToWaiting);
      socket.off('player-answered', handlePlayerAnswered);
      socket.off('answer-received', handleAnswerReceived);
    };
  }, [socket, hostIsPlaying]);

  useEffect(() => {
    if (!currentQuestion || questionResult) return;

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 100) {
          clearInterval(interval);
          return 0;
        }
        return prev - 100;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [currentQuestion, questionResult]);

  const handleAnswerClick = (answer: string) => {
    if (answerSubmitted || !currentQuestion || isSubmittingRef.current) return;
    
    isSubmittingRef.current = true;
    setSelectedAnswer(answer);

    const questionId = currentQuestion.questionId || currentQuestion.id;
    if (!questionId) {
      isSubmittingRef.current = false;
      return;
    }

    socket.emit('submit-answer', {
      roomName: roomNameRef.current,
      questionId,
      answer,
      timestamp: Date.now(),
    });
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer && currentQuestion && !answerSubmitted && !isSubmittingRef.current) {
      handleAnswerClick(selectedAnswer);
    }
  };

  const handleRestartGame = () => {
    if (socket && roomState) {
      setWaitingForNewGame(true);
      setGameEnded(false);
      setFinalLeaderboard([]);
      setCurrentQuestion(null);
      setQuestionResult(null);
      resetHostPlayingState();
      socket.emit('start-game', { roomName: roomState.roomName });
    }
  };

  // Convert liveAnswers to shared format
  const sharedLiveAnswers: SharedLiveAnswer[] = liveAnswers.map(la => ({
    playerId: la.playerId,
    playerName: la.playerName,
    avatar: la.avatar,
    timestamp: la.timestamp,
    answer: la.answer,
  }));

  // Game ended screen
  if (gameEnded) {
    const handlePlaySameGame = () => {
      if (socket && roomState) {
        setGameEnded(false);
        setFinalLeaderboard([]);
        setCurrentQuestion(null);
        setQuestionResult(null);
        setWaitingForNewGame(true);
        socket.emit('start-game', { roomName: roomState.roomName });
      }
    };

    return (
      <GameEndScreen
        finalLeaderboard={finalLeaderboard}
        gameStatistics={gameStatistics}
        onPlaySameGame={handlePlaySameGame}
        onPlayAnotherGame={onBackToGameSelection || (() => {})}
        onBackToGameSelection={onBackToGameSelection}
        roomName={roomState.roomName}
        gameType={roomState.gameType}
      />
    );
  }

  // Waiting state
  if (waitingForNewGame || (!currentQuestion && !questionResult)) {
    return (
      <div style={{ minHeight: '100vh', width: '100%', background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)', display: 'flex', flexDirection: 'column' }}>
        <Header
          leftButton={onBackToGameSelection ? { icon: HeaderIcons.back, onClick: onBackToGameSelection, tooltip: t('gameplay.backToGameSelection') } : undefined}
          rightButton={{ icon: HeaderIcons.refresh, onClick: () => window.location.reload(), tooltip: t('common.tooltips.refreshPage') }}
          roomCode={roomState.roomName}
        />
        <div style={{ flex: 1, padding: '2.5rem 2rem', paddingTop: '6rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>🔥</div>
          <h2 style={{ fontSize: '2rem' }}>{t('gameplay.hostWaitingNextQuestion')}</h2>
        </div>
      </div>
    );
  }

  // Question result screen
  if (questionResult) {
    return (
      <div style={{ minHeight: '100vh', width: '100%', background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)', display: 'flex', flexDirection: 'column' }}>
        <Header
          leftButton={onBackToGameSelection ? { icon: HeaderIcons.back, onClick: onBackToGameSelection, tooltip: t('gameplay.backToGameSelection') } : undefined}
          rightButton={{ icon: HeaderIcons.refresh, onClick: () => window.location.reload(), tooltip: t('common.tooltips.refreshPage') }}
          roomCode={roomState.roomName}
        />
        <div style={{ flex: 1, padding: '2.5rem 2rem', paddingTop: '6rem', display: 'flex', flexDirection: 'column' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
            <div style={{ background: 'rgba(52, 211, 153, 0.15)', border: '3px solid rgba(52, 211, 153, 0.3)', borderRadius: '1.5rem', padding: 'clamp(1.5rem, 4vw, 2rem)', textAlign: 'center', marginBottom: '2rem' }}>
              <div style={{ fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', opacity: 0.7, marginBottom: '0.5rem', color: '#fff' }}>{t('gameplay.correctAnswerLabel')}</div>
              <div style={{ fontSize: 'clamp(1.8rem, 5vw, 2.5rem)', fontWeight: 'bold', color: '#34D399' }}>
                {questionResult.correctAnswer}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Question display - fullscreen
  const progress = currentQuestion ? (timeRemaining / currentQuestion.timeLimit) * 100 : 0;
  const secondsRemaining = Math.ceil(timeRemaining / 1000);

  return (
    <div className="app">
      <Header
        leftButton={{
          icon: HeaderIcons.back,
          onClick: onBackToGameSelection || (() => {}),
          tooltip: t('gameplay.backToGameSelection')
        }}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: handleRestartGame,
          tooltip: t('gameplay.restartGame')
        }}
        centerContent={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div className="header-game-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>{t('gameplay.partyHost.gameLabel')}</span>
              <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#F8FAFC' }}>
                🔥 Kinky or Not?
              </span>
            </div>
            <div className="header-game-name" style={{ 
              width: '1px', 
              height: '24px', 
              backgroundColor: 'rgba(255, 255, 255, 0.3)' 
            }}></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>{t('gameplay.partyHost.roomCodeLabel')}</span>
              <div style={{ 
                fontSize: '1.3rem', 
                fontWeight: 'bold', 
                letterSpacing: '0.15rem',
                color: THEME.primary,
                padding: '0.3rem 0.8rem',
                backgroundColor: THEME.primaryLight,
                borderRadius: '6px',
                border: `2px solid ${THEME.primaryBorder}`,
              }}>
                {roomState.roomName}
              </div>
            </div>
          </div>
        }
      />

      {/* Main game layout - desktop: left (question) | right (standings), mobile: stacked */}
      {currentQuestion && (
        <div className="game-host-layout">
          {/* Left side: Question and Answers (2/3 width). When host not playing, no answers (host-only). */}
          <div className={`game-host-left ${!currentQuestion.imageUrl && !currentQuestion.fallbackImageUrl ? 'no-image' : ''} ${!hostIsPlaying ? 'host-only' : ''}`}>
            <div className="game-host-timer-section">
              <TimerSection
                questionNumber={currentQuestion.questionNumber}
                totalQuestions={currentQuestion.totalQuestions}
                timeSeconds={secondsRemaining}
                progress={progress}
                primaryColor={THEME.primary}
              />
            </div>
            
            {/* Image section - only if image exists */}
            {(currentQuestion.imageUrl || currentQuestion.fallbackImageUrl) && (
              <div className="game-host-image-section">
                <div className="game-image-container">
                  <ImageWithFallback
                    src={currentQuestion.imageUrl || ''}
                    fallbackSrc={currentQuestion.fallbackImageUrl}
                    alt={t('gameplay.imageAlt.guessPicture')}
                  />
                </div>
              </div>
            )}
            
            {/* Question section with flip animation */}
            <div className={`game-host-question-section${flipState.showQuestionFlip || flipState.isFadingOut ? ' game-host-question-section--showing-fact' : ''}`}>
              <QuestionDisplay
                question={currentQuestion as Question}
                showQuestionFlip={flipState.showQuestionFlip}
                isFadingOut={flipState.isFadingOut}
                previousQuestionIdForFlipRef={flipState.previousQuestionIdForFlipRef}
                showHint={false}
                gameType={roomState.gameType}
              />
            </div>
            
            {/* Answers section */}
            {/* Answers section – only when host is playing */}
            {hostIsPlaying && (
              <div className="game-host-answers-section">
                <AnswerOptions
                  question={currentQuestion as Question}
                  selectedAnswer={selectedAnswer}
                  answerSubmitted={answerSubmitted}
                  hostIsPlaying={hostIsPlaying}
                  showHint={false}
                  gameType={roomState.gameType}
                  onAnswerSelect={(answer) => {
                    if (!answerSubmitted && !isSubmittingRef.current) {
                      setSelectedAnswer(answer);
                    }
                  }}
                  onSubmitAnswer={() => {
                    if (selectedAnswer && currentQuestion && !answerSubmitted && !isSubmittingRef.current) {
                      handleAnswerClick(selectedAnswer);
                    }
                  }}
                  answerCorrect={answerCorrect}
                  waitingForOthers={answerSubmitted && !questionResult}
                  isSubmitting={isSubmittingRef.current}
                />
              </div>
            )}
          </div>
          
          {/* Right side: Standings and Live Answers (1/3 width) */}
          <div className="game-host-right">
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={sharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={currentQuestion.questionId || currentQuestion.id}
              shouldUpdateScores={false}
              theme={{
                primary: THEME.primary,
                primaryLight: THEME.primaryLight,
                accent: THEME.accent,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default KinkyOrNotHostDisplay;
